"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Receipt, ReceiptService } from "../services/receipt-service";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
import { ReceiptTable } from "../components/receipt-table";
import { ReceiptCategory, ReceiptCategoryService } from "../../receipt-categories/services/receipt-category-service";

interface Props {
  search?: string;
  reload?: number;
  typeFilter?: string | number | null;
  onEdit?: (item: Receipt) => void;
  onView?: (item: Receipt) => void;
  onLoadedCount?: (count: number) => void;
}

export const ActiveReceiptTab: React.FC<Props> = ({ search = "", reload = 0, typeFilter = "", onEdit, onView, onLoadedCount }) => {
  const [rows, setRows] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);

  const { fetch: list } = ReceiptService.list();
  const { fetch: changeStatus } = ReceiptService.changeStatus();
  const { fetch: listCategories } = ReceiptCategoryService.list();
  const listRef = useRef(list);
  useEffect(() => { listRef.current = list; }, [list]);
  const onLoadedCountRef = useRef(onLoadedCount);
  useEffect(() => { onLoadedCountRef.current = onLoadedCount; }, [onLoadedCount]);
  const [categoryMap, setCategoryMap] = useState<Record<number, string>>({});
  const listCategoriesRef = useRef(listCategories);
  useEffect(() => { listCategoriesRef.current = listCategories; }, [listCategories]);

  useEffect(() => {
    (async () => {
      const res = await listCategoriesRef.current({ status: -1, limit: 2000 } as any);
      if (res && isSuccess(res)) {
        const d: any = res.data || {};
        const list: ReceiptCategory[] = Array.isArray(d.list) ? d.list : [];
        const map: Record<number, string> = {};
        list.forEach((c) => { if (c?.id != null) map[Number(c.id)] = `${c.code ?? ''}${c.code ? ' - ' : ''}${c.name}`; });
        setCategoryMap(map);
      }
    })();
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { status: 1, page, limit };
      if (search) params.key_search = search;
      if (typeFilter) params.type = Number(typeFilter);
      const res = await listRef.current(params);
      if (res && isSuccess(res)) {
        const d: any = res.data || {};
        const list: Receipt[] = Array.isArray(d.list) ? d.list : [];
        setRows(list);
        const total = Number(d.total_record ?? d.totalRecord ?? d.total ?? d.totalRecords ?? list.length ?? 0);
        setTotal(total);
        onLoadedCountRef.current?.(total);
      } else {
        setRows([]); setTotal(0); onLoadedCountRef.current?.(0);
        showToast({ message: res?.message ?? "Không thể tải dữ liệu", type: "error" });
      }
    } finally { setLoading(false); }
  }, [search, typeFilter, page, limit]);

  useEffect(() => { void loadData(); }, [reload, search, loadData]);
  useEffect(() => { setPage(1); }, [limit]);

  const handleDeactivate = async (item: Receipt) => {
    const res = await changeStatus(item.id, { status: 4 });
    if (res && isSuccess(res)) { showToast({ message: "Đã tạm ngưng phiếu", type: "success" }); void loadData(); }
    else showToast({ message: res?.message ?? "Không thể cập nhật trạng thái", type: "error" });
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const start = Math.max(1, page - 1);
    const end = Math.min(totalPages, page + 1);
    pages.push(1);
    if (start > 2) pages.push("...");
    for (let p = start; p <= end; p++) if (p !== 1 && p !== totalPages) pages.push(p);
    if (end < totalPages - 1) pages.push("...");
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  return (
    <div>
      <ReceiptTable data={rows} loading={loading} isInactive={false} startIndex={(page-1)*limit} categoryNameMap={categoryMap} onEdit={onEdit} onView={onView} onChangeStatus={handleDeactivate} />

      {/* Pagination */}
      <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Info */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Hiển thị</span>
            <select
              className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 cursor-pointer"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>trên tổng {total.toLocaleString()} kết quả</span>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center gap-1">
            <button
              className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {getPageNumbers().map((p, i) =>
              typeof p === "number" ? (
                <button
                  key={i}
                  onClick={() => setPage(p)}
                  className={`min-w-[36px] h-9 px-3 rounded-lg text-sm font-medium transition-colors ${
                    p === page
                      ? "bg-green-600 text-white shadow-sm"
                      : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {p}
                </button>
              ) : (
                <span key={i} className="px-2 text-gray-400">...</span>
              )
            )}

            <button
              className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
